import { useEffect, useMemo, useState, useCallback } from 'react'
import { useInfiniteBrandKitMediaFiles, IMediaFile } from 'hooks/queries/brandKits/useInfiniteBrandKitMediaFiles'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { collectResults } from 'utils/query'
import { SelectedImage } from 'types'

const MIN_SIZE = 128

interface UseMediaSelectionParams {
  brandKitId: number | undefined
  selectedMediaFileId: number | null
  onSelectMediaFile: (id: number) => void
}

export const useMediaSelection = ({ brandKitId, selectedMediaFileId, onSelectMediaFile }: UseMediaSelectionParams) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const [uploadProgress, setUploadProgress] = useState(0)

  const mediaFilesQuery = useInfiniteBrandKitMediaFiles({
    brandKitId,
    items: 9,
    mime_types: 'image/jpeg,image/png,image/webp',
  })

  const { uploadImageFilesWithoutLoadingToast, isUploading } = useImportMediaFiles(
    false,
    () => setUploadProgress(0),
    (progress) => setUploadProgress(progress),
    { forceExtractBackgroundJobSync: true },
  )

  const rawMediaFiles = collectResults(mediaFilesQuery, (data) => data.media_files)

  const mediaFiles = useMemo(() => {
    return rawMediaFiles.filter((file) => {
      // Filter out files smaller than 128x128, necessary for Exactly
      if (file.width && file.height) {
        return file.width >= MIN_SIZE && file.height >= MIN_SIZE
      }
      // If dimensions unknown, include the file
      return true
    })
  }, [rawMediaFiles])

  const isLoading = mediaFilesQuery.isLoading

  // Auto-select first media file when loaded
  useEffect(() => {
    if (isLoading) return
    if (selectedMediaFileId !== null) return
    if (mediaFiles.length > 0) {
      onSelectMediaFile(mediaFiles[0].media_file_id)
    }
  }, [isLoading, mediaFiles, selectedMediaFileId, onSelectMediaFile])

  const handleSelectMediaFile = useCallback(
    (image: SelectedImage) => {
      const mediaFile = mediaFiles.find(
        (f) => f.source_url === image.url || f.thumbnail_url === image.url || f.source_url === image.source_url,
      )
      if (mediaFile) {
        onSelectMediaFile(mediaFile.media_file_id)
      }
    },
    [mediaFiles, onSelectMediaFile],
  )

  const handleUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || !brandKitId) return null
      const uploadedFiles = await uploadImageFilesWithoutLoadingToast(Array.from(files), undefined, {
        brand_kit_id: brandKitId,
      })
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
      queryClient.invalidateQueries({
        queryKey: ['workspaces', workspaceId, 'brandKits', brandKitId, 'mediaFiles', 'list'],
        exact: false,
      })
      return uploadedFiles
    },
    [brandKitId, uploadImageFilesWithoutLoadingToast, queryClient, workspaceId],
  )

  return {
    // Data
    mediaFiles,
    rawMediaFiles,

    // Loading/upload state
    isLoading,
    isUploading,
    uploadProgress,

    // Handlers
    handleSelectMediaFile,
    handleUpload,
  }
}

// Utility function to convert IMediaFile to SelectedImage
export const mediaFileToSelectedImage = (file: IMediaFile): SelectedImage => ({
  url: file.thumbnail_url || file.source_url,
  id: String(file.media_file_id),
  source_url: file.source_url,
  thumbnail_url: file.thumbnail_url,
  mime_type: file.mime_type,
  width: file.width,
  height: file.height,
})
